// pfs-select-field.ts — self-contained Select-Control (PFS-619, Epic „Universale Modals").
//
// Wie <pfs-text-field>, nur mit einem <select> in der EIGENEN Shadow. Die
// Optionen kommen als Property (el.options), NICHT geslottet → alles bleibt in
// der Control-Shadow und damit immun gegen Dokument-CSS (WP/Tailwind).
//
// API:
//   Property: el.options = [{value, label} | "string", …] · el.value (get/set).
//   Attribute (data-*): data-label · data-name · data-required · data-error ·
//     data-hint · data-value (INITIAL) · data-readonly (zeigt das gewaehlte Label
//     als Wert-Box) · data-inline (kompakte Inline-Variante: :host inline-block,
//     auto-width, kleineres select, Label+Meldung ausgeblendet — fuer Inline-
//     Picker in einer Tabellenzelle statt der Block-Form-Optik).
//   Events: re-emittiert `input` + `change` als bubbles+composed vom Host.
import { defineComponent } from "../../composition/define-component.js";
import { withShadow } from "../../composition/with-shadow.js";
import { takeOwnProperty } from "../../utils/upgrade-property.js";
import {
  installFieldValue,
  bindFieldControl,
  reapplyFieldValue,
} from "../../composition/field-value.js";
import { CONTROL_CSS } from "../../styles/control.js";

const STYLES = `
:host { display: flex; flex-direction: column; gap: var(--pfs-space-label, 0.5rem); }
.field { display: flex; flex-direction: column; gap: var(--pfs-space-label, 0.5rem); }
.lbl { display: block; font-size: 0.875rem; font-weight: 500; color: var(--pfs-color-on-surface); }
.lbl:empty { display: none; }
.req { color: var(--pfs-color-danger); }
${CONTROL_CSS}
:host([data-error]:not([data-error=""])) select { border-color: var(--pfs-color-danger); }
.ro {
  padding: 0.5rem 0.6rem; box-sizing: border-box;
  background: var(--pfs-color-surface-sunken);
  border: 1px solid var(--pfs-color-outline);
  border-radius: var(--pfs-radius-sm, 0.375rem);
  color: var(--pfs-color-on-surface-muted); font-size: 0.9rem;
}
.msg { font-size: 0.8rem; }
.msg:empty { display: none; }
.msg.error { color: var(--pfs-color-danger); }
.msg.hint { color: var(--pfs-color-on-surface-muted); }
/* Inline-Variante: kompakter Picker fuer eine Tabellenzelle (statt Block-Form).
   Nur additiv — Standard-Optik unberuehrt. */
:host([data-inline]) { display: inline-block; }
:host([data-inline]) .lbl,
:host([data-inline]) .msg { display: none; }
:host([data-inline]) select { width: auto; padding: 0.2rem 0.4rem; font-size: 0.8rem; }
`;

interface Opt {
  value: string;
  label: string;
}

/** Option-Eingabeform: nackter String ODER {value,label}. */
export type SelectOption = string | { value: string | number; label?: string };

/** Typisierte Consumer-Sicht auf <pfs-select-field> — Consumer setzen
 *  `(createElement("pfs-select-field") as PfsSelectFieldElement).options = […]`. */
export interface PfsSelectFieldElement extends HTMLElement {
  options: SelectOption[];
  value: string;
}

const normalize = (o: unknown): Opt => {
  if (typeof o === "string") return { value: o, label: o };
  const r = o as { value?: unknown; label?: unknown };
  const value = r.value == null ? "" : String(r.value);
  return { value, label: r.label == null ? value : String(r.label) };
};

defineComponent({
  tagName: "pfs-select-field",
  mixins: [withShadow({ mode: "open", delegatesFocus: true })],
  // .value ab Konstruktion les-/schreibbar (pre-connect-sicher, PFS-645).
  created: installFieldValue,
  render(host, ctx) {
    const root = ctx.shadowRoot;
    if (!root) return;
    const style = document.createElement("style");
    style.textContent = STYLES;
    root.appendChild(style);

    const label = document.createElement("label");
    label.className = "field";
    const lblEl = document.createElement("span");
    lblEl.className = "lbl";
    const select = document.createElement("select");
    const roEl = document.createElement("div");
    roEl.className = "ro";
    label.append(lblEl, select, roEl);
    root.appendChild(label);
    const msg = document.createElement("p");
    msg.className = "msg";
    root.appendChild(msg);

    let opts: Opt[] = [];

    // Vorab (vor dem Upgrade) gesetzte options-Property einsammeln, um sie NACH
    // dem defineProperty erneut durch den Setter zu schleusen. (value wird
    // pre-connect-sicher von installFieldValue/.created gepuffert.)
    const self = host as PfsSelectFieldElement;
    const preOptions = takeOwnProperty<SelectOption[]>(host, "options");

    const renderOpts = (): void => {
      select.textContent = "";
      for (const o of opts) {
        const el = document.createElement("option");
        el.value = o.value;
        el.textContent = o.label;
        select.appendChild(el);
      }
      // Auswahl nach Options-Rebuild wiederherstellen (gewuenschter Wert lebt im
      // field-value-Puffer, ueberlebt so den Neuaufbau).
      reapplyFieldValue(host);
    };

    Object.defineProperty(host, "options", {
      configurable: true,
      get: () => opts,
      set: (v: unknown) => {
        opts = Array.isArray(v) ? v.map(normalize) : [];
        renderOpts();
      },
    });

    // el.value an das <select> binden: wendet den gewuenschten Wert an, haelt den
    // Puffer bei Auswahl synchron und malt bei jeder Wert-Aenderung die
    // Readonly-Box neu (onValueApplied-Naht).
    bindFieldControl(host, select, paintReadonly);

    // vorab gesetzte options jetzt anwenden (nach dem Bind → renderOpts kann die
    // gepufferte Auswahl direkt wiederherstellen).
    if (preOptions !== undefined) self.options = preOptions;

    // composed input/change vom Host re-emittieren (Buffer-Sync + Readonly-Repaint
    // erledigt bindFieldControl bereits ueber eigene Listener).
    const reemit = (type: string) => (): void => {
      host.dispatchEvent(new Event(type, { bubbles: true, composed: true }));
    };
    select.addEventListener("input", reemit("input"));
    select.addEventListener("change", reemit("change"));

    function paintReadonly(): void {
      const ro = host.dataset.readonly != null;
      select.style.display = ro ? "none" : "";
      select.disabled = ro;
      roEl.style.display = ro ? "" : "none";
      if (ro) roEl.textContent = opts.find((o) => o.value === select.value)?.label ?? select.value;
    }

    const paint = (): void => {
      const labelText = host.dataset.label ?? "";
      lblEl.textContent = labelText;
      if (host.dataset.required != null && labelText) {
        const s = document.createElement("span");
        s.className = "req";
        s.textContent = "*";
        lblEl.appendChild(s);
      }
      if (host.dataset.name != null) select.name = host.dataset.name;
      paintReadonly();

      const err = (host.dataset.error ?? "").trim();
      const hint = (host.dataset.hint ?? "").trim();
      if (err) {
        msg.textContent = err;
        msg.className = "msg error";
      } else if (hint) {
        msg.textContent = hint;
        msg.className = "msg hint";
      } else {
        msg.textContent = "";
        msg.className = "msg";
      }
    };
    paint();

    const obs = new MutationObserver(paint);
    obs.observe(host, {
      attributes: true,
      attributeFilter: [
        "data-label",
        "data-required",
        "data-error",
        "data-hint",
        "data-readonly",
        "data-name",
      ],
    });
    ctx.cleanup.push(() => obs.disconnect());
  },
});
